import {
  addMinutes,
  addSeconds,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from "date-fns";
import Cookies from "js-cookie";
import { isRemainingTimeType } from "@circle-react/helpers/paywallCustomizationHelpers";
import type { PaywallCustomization } from "@circle-react/types/PaywallCustomization";

interface CountdownCookie {
  finish_at: string;
}

export const formatCountdownTimer = (
  endDateString: string,
  setExceedTime?: (input: number) => void,
) => {
  const endDateTime = new Date(endDateString);
  const now = new Date();

  if (endDateTime <= now) {
    setExceedTime && setExceedTime(Math.abs(Number(now) - Number(endDateTime)));
    return "0s";
  }

  const days = differenceInDays(endDateTime, now);
  const hours = differenceInHours(endDateTime, now) % 24;
  const minutes = differenceInMinutes(endDateTime, now) % 60;
  const seconds = differenceInSeconds(endDateTime, now) % 60;

  const isLastHour = days === 0 && hours === 0;

  // Show seconds if less than 1 hour left
  if (isLastHour) {
    return `${minutes}m : ${seconds}s`;
  }

  if (days > 0) {
    return `${days}d : ${hours}h : ${minutes}m`;
  }

  return `${hours}h : ${minutes}m`;
};

export const getCookieFallback = (
  countdownProperties: PaywallCustomization["countdown_properties"],
): CountdownCookie => {
  if (isRemainingTimeType(countdownProperties.countdown_type)) {
    return {
      finish_at:
        countdownProperties.remaining_time_interval === "minutes"
          ? addMinutes(
              new Date(),
              countdownProperties.remaining_time || 1,
            ).toISOString()
          : addSeconds(
              new Date(),
              countdownProperties.remaining_time || 1,
            ).toISOString(),
    };
  }

  return {
    finish_at: countdownProperties.finish_at ?? "",
  };
};

export const parseCookie = (paywallId?: number): CountdownCookie | null => {
  const countdownCookie = Cookies.get(`checkout_countdown_${paywallId}`);

  if (!countdownCookie) {
    return null;
  }

  try {
    return JSON.parse(countdownCookie);
  } catch {
    console.error("Can't parse the Countdown data");
    return null;
  }
};
